<script setup lang="ts">
import {computed} from 'vue';
import {SubjectDataType} from "@utilities/reports/types";
import {useReporting} from '@utilities/reports/composables/useReporting';
import {useTableHighlight} from '@utilities/reports/composables/useTableHighlight';

// Initialize store and composables
const {daysInMonth, hasDataForDay, isEodSubmitted, isOpen, getValueForDay, reportData} = useReporting('stat');
const {
  setHoveredRow,
  setHoveredCol,
  clearHoveredRow,
  clearHoveredCol,
  isRowHovered,
  isColHovered
} = useTableHighlight();

// Transform KPI data for table display
const tableData = computed(() => {
  if (!reportData.value || !reportData.value.data) return [];

  const rows = [];
  const data = reportData.value.data;

  // Define human-readable names for indicators
  const indicatorNames = {
    open: 'Open',
    eodSubmitted: 'EOD Submitted',
    totalSales: 'Total Sales',
    numSales: 'Number of Sales',
    cash: 'Cash',
    cc: 'CC',
    numServices: 'Services Performed',
    numSmogs: 'Number of Smogs',
    totalServiceSales: 'Total Service Sales',
    totalSmogSales: 'Total Smog Sales',
  };

  // Create a row for each indicator
  Object.keys(data).forEach(indicator => {
    if (['dates', 'open', 'eodSubmitted'].includes(indicator)) return;

    // For the "normal" indicators
    if (Array.isArray(data?.[indicator]?.daily)) {
      const row = {
        key: indicator,
        indicator: indicatorNames[indicator] ?? indicator,
        overall: data[indicator].overall ?? 'N/A',
        daily: data[indicator].daily ?? [],
        type: 'normal'
      };

      rows.push(row);
    } else {
      // Add section headers for the tallies
      if (indicator === 'serviceSalesTally') {
        rows.push({
          key: 'serviceSalesHeader',
          indicator: 'Service Sales',
          overall: '',
          daily: [],
          type: 'header'
        });
      } else if (indicator === 'serviceTypeTally') {
        rows.push({
          key: 'serviceTypesHeader',
          indicator: 'Service Types',
          overall: '',
          daily: [],
          type: 'header'
        });
      } else if (indicator === 'smogTally') {
        rows.push({
          key: 'smogSalesHeader',
          indicator: 'Smog Sales',
          overall: '',
          daily: [],
          type: 'header'
        });
      }

      for (const [key, value] of Object.entries(data[indicator])) {

        // Check if the value has the expected structure
        if (!value || typeof value !== 'object') continue;

        // Type assertion to help TypeScript understand the structure
        const tallyValue = value as SubjectDataType<any>;

        // Verify that required properties exist
        if (!('daily' in tallyValue) || !('overall' in tallyValue)) continue;

        const row = {
          key,
          indicator: key,
          overall: tallyValue.overall ?? 'N/A',
          daily: tallyValue.daily ?? [],
          type: 'tally',
          tallyType: indicator
        };

        rows.push(row);
      }
    }
  });

  console.log("Rows: ", rows);

  return rows;
});

</script>

<template>
  <div class="flex flex-col gap-4 w-full px-4 mt-4 items-center">
    <!-- Table container with horizontal scroll -->
    <div class="w-full justify-start">
      <div
          class="relative overflow-x-auto max-h-[calc(100vh-400px)] overflow-y-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-white0 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-white0 border-slate-200">
        <!-- Table with Tailwind classes -->
        <table class="w-full border-collapse border border-slate-200 table-fixed">
          <!-- Table Header -->
          <thead class="sticky top-0 z-30">
          <tr>
            <!-- Fixed "Overall" column -->
            <th
                class="sticky left-0 top-0 z-40 bg-white border border-slate-200 p-2 w-32 text-center"
                @mouseover="setHoveredCol(0)"
                @mouseout="clearHoveredCol()"
            >
              Overall
            </th>

            <!-- Fixed "KPI" column -->
            <th
                class="sticky left-32 top-0 z-40 bg-primary-500 text-white border border-slate-200 p-2 w-48 text-center"
                @mouseover="setHoveredCol(1)"
                @mouseout="clearHoveredCol()"
            >
              STAT
            </th>

            <!-- Day number columns -->
            <th
                v-for="day in daysInMonth"
                :key="day"
                class="sticky top-0 z-30 border border-slate-200 p-2 w-24 text-center"
                :class="{
                  'bg-white': !isColHovered(day+1) && hasDataForDay(day) && isEodSubmitted(day) && isOpen(day),
                  'bg-slate-100': (isColHovered(day + 1) && isEodSubmitted(day)) || !isOpen(day),
                  'bg-slate-400': !hasDataForDay(day),
                  'bg-amber-100': hasDataForDay(day) && !isEodSubmitted(day) && isOpen(day) && !isColHovered(day + 1),
                  'bg-amber-200': (!isEodSubmitted(day) && isColHovered(day + 1))
                 }"
                @mouseover="setHoveredCol(day + 1)"
                @mouseout="clearHoveredCol()"
            >
              {{ day }}
            </th>
          </tr>
          </thead>

          <!-- Table Body -->
          <tbody>
          <tr
              v-for="(row, rowIndex) in tableData"
              :key="row.key"
              :class="{ 
                'bg-slate-100': isRowHovered(rowIndex) && row.type !== 'header',
                'bg-white': row.type === 'header'
              }"
              @mouseover="row.type !== 'header' && setHoveredRow(rowIndex)"
              @mouseout="row.type !== 'header' && clearHoveredRow()"
          >
            <!-- Overall value column (fixed) -->
            <td
                class="sticky left-0 z-10 border border-slate-200 p-2 w-20 text-center"
                :class="{ 
                  'bg-white': row.type !== 'header',
                  'bg-slate-100': (isColHovered(0) || isRowHovered(rowIndex)) && row.type !== 'header'
                }"
                @mouseover="row.type !== 'header' && setHoveredCol(0)"
                @mouseout="row.type !== 'header' && clearHoveredCol()"
            >
              {{ row.overall }}
            </td>

            <!-- Indicator name column (fixed) -->
            <td
                class="sticky left-32 z-10 font-normal text-center border border-slate-200 p-2 w-48"
                :class="{
                  'bg-primary-500 text-white': row.type !== 'header',
                  'bg-white text-primary-800 font-bold': row.type === 'header'
                }"
                @mouseover="row.type !== 'header' && setHoveredCol(1)"
                @mouseout="row.type !== 'header' && clearHoveredCol()"
            >
              {{ row.indicator }}
            </td>

            <!-- Daily value columns -->
            <td
                v-for="day in daysInMonth"
                :key="day"
                class="border border-slate-200 p-2 w-24 text-center overflow-hidden"
                :class="{
                'bg-slate-100': ((isColHovered(day + 1) && isEodSubmitted(day)) || (isRowHovered(rowIndex) && isEodSubmitted(day)) || !isOpen(day)) && row.type !== 'header',
                'bg-slate-400': !hasDataForDay(day) && row.type !== 'header',
                'bg-amber-100': hasDataForDay(day) && !isEodSubmitted(day) && isOpen(day) && !isColHovered(day + 1) && !isRowHovered(rowIndex) && row.type !== 'header',
                'bg-amber-200': (!isEodSubmitted(day) && (isRowHovered(rowIndex) || isColHovered(day + 1))) && row.type !== 'header',
                'bg-white': row.type === 'header'
              }"
                @mouseover="row.type !== 'header' && setHoveredCol(day + 1)"
                @mouseout="row.type !== 'header' && clearHoveredCol()"
            >
              <template
                  v-if="(isEodSubmitted(day) || row.key === 'numIssues' || ![0, '$0.00', '0%'].includes(getValueForDay(row, day))) && row.type !== 'header'">
                <!-- Display value if EOD is submitted or if it's a specific indicator -->
                {{ getValueForDay(row, day) }}
              </template>
              <i v-else-if="isOpen(day) && row.type !== 'header'" class="pi pi-exclamation-circle text-amber-600"/>
              <i v-else-if="row.type !== 'header'" class="pi pi-circle text-slate-300"></i>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
